import type { ROLES } from "containers/ScopeContext/roles.enum";

export type UserAttributes = { Name: string; Value: any }[];

export interface IUser {
    // @deprecated types
    Username: string;
    Attributes: UserAttributes;
    UserCreateDate: Date;
    UserLastModifiedDate: Date;
    Enabled: boolean;
    UserStatus: string;

    // new types
    id: string;
    active: number;
    avatarUrl?: string;
    companyId: number;
    created: Date;
    description?: string;
    email: string;
    firstname: string;
    hasAcceptedToS: 0;
    isVerified: 0;
    lang: "fr-FR" | "fr-BE" | "en-US" | "fr" | "en";
    lastname?: string;
    modified: Date;
    role: string;
    syncToken?: string;
    type: string;
    userId: number;
    uuid: string;
    jobTitle: string | null;
}

export interface IconProps {
    color?: string;
    width?: string | number;
    height?: string | number;
    stroke?: string;
    onClick?: (() => void) | ((e: React.MouseEvent<any>) => void);
    style?: Record<string, any>;
    className?: string;
}

// As of 12th of April 2024, for POST requests, they made a typo for title
export enum BexioContactType {
    PERSON = 1,
    COMPANY = 2,
}

export enum BexioMWSTType {
    INCLUDING_TAXES = 0,
    EXCLUDING_TAXES = 1,
    EXEMPT_FROM_TAXES = 2,
}

export enum BexioItemStatusId {
    DRAFT = 7,
    PENDING,
    PAID,
    PARTIAL = 16,
    CANCELLED = 19,
    UNPAID = 31,
}

export enum BexioPaymentType {
    ADVANCE_PAYMENT = 1,
    CASH,
    DEBIT_CARD,
    INVOICE,
    CASH_ON_DELIVERY,
    CREDIT_CARD,
    DIRECT_DEBIT_LSV,
}

export enum BexioLanguageId {
    DUTCH = 1,
    FRENCH,
    ITALIAN,
    ENGLISH,
    PORTUGUESE,
    CHINESE,
    JAPANESE,
    SPANISH,
}

/**
 * @typedef {object} DTOBexioPOSTContact
 *
 * @property {number} id - The contact ID
 * @property {string} nr - The contact number
 * @property {BexioContactType} contact_type_id - The contact type
 * @property {string} name_1 - The first name
 * @property {string | null} name_2 - The last name
 * @property {number | null} salutation_id - The salutation ID
 * @property {number | null} salutation_form - The salutation form
 * @property {number | null} titel_id - The title ID (typo)
 * @property {Date | string | null} birthday - The birthday
 * @property {string | null} address - The address
 * @property {number | null} postcode - The postcode
 * @property {string | null} city - The city
 * @property {number | null} country_id - The country ID
 * @property {string | null} mail - The primary email address
 * @property {string | null} mail_second - The secondary email address
 * @property {string | null} phone_fixed - The primary fixed phone number
 * @property {string | null} phone_fixed_second - The secondary fixed phone number
 * @property {string | null} phone_mobile - The mobile phone number
 * @property {string | null} fax - The fax number
 * @property {string | null} url - The URL
 * @property {string | null} skype_name - The Skype username
 * @property {string | null} remarks - Any additional remarks or notes
 * @property {BexioLanguageId | null} language_id - The language ID
 * @property {boolean} is_lead - Indicates if the contact is a lead
 * @property {string | null} contact_group_ids - The IDs of contact groups
 * @property {number | null} contact_branch_ids - The IDs of contact branches
 * @property {number | null} user_id - The user ID
 * @property {number | null} owner_id - The owner ID
 * @property {Date | string} updated_at - The date when the contact was last updated
 */
export type DTOBexioPOSTContact = {
    id: number;
    nr: string;
    contact_type_id: BexioContactType.PERSON;
    name_1: string;
    name_2: string | null;
    salutation_id: number | null;
    salutation_form: number | null;
    titel_id: number | null; // typo
    birthday: Date | string | null;
    address: string | null;
    postcode: number | null;
    city: string | null;
    country_id: number | null;
    mail: string | null;
    mail_second: string | null;
    phone_fixed: string | null;
    phone_fixed_second: string | null;
    phone_mobile: string | null;
    fax: string | null;
    url: string | null;
    skype_name: string | null;
    remarks: string | null;
    language_id: BexioLanguageId | null;
    is_lead: boolean;
    contact_group_ids: string | null;
    contact_branch_ids: number | null;
    user_id: number | null;
    owner_id: number | null;
    updated_at: Date | string;
};

/**
 * @typedef {object} DTOBexioGETContact
 *
 * @property {number} id - The contact ID
 * @property {string} nr - The contact number
 * @property {number} contact_type_id - The contact type
 * @property {string} name_1 - The first name
 * @property {string | null} name_2 - The last name
 * @property {number | null} salutation_id - The salutation ID (as per BexioSalutationId)
 * @property {number | null} salutation_form - The salutation form
 * @property {number | null} title_id - The title ID
 * @property {Date | string | null} birthday - The birthday
 * @property {string | null} address - The address
 * @property {number | null} postcode - The postcode
 * @property {string | null} city - The city
 * @property {number | null} country_id - The country ID
 * @property {string | null} mail - The primary email address
 * @property {string | null} mail_second - The secondary email address
 * @property {string | null} phone_fixed - The primary fixed phone number
 * @property {string | null} phone_fixed_second - The secondary fixed phone number
 * @property {string | null} phone_mobile - The mobile phone number
 * @property {string | null} fax - The fax number
 * @property {string | null} url - The URL
 * @property {string | null} skype_name - The Skype username
 * @property {string | null} remarks - Any additional remarks or notes
 * @property {number | null} language_id - The language ID (as per BexioLanguageId)
 * @property {boolean} is_lead - Indicates if the contact is a lead
 * @property {string | null} contact_group_ids - The IDs of contact groups
 * @property {number | null} contact_branch_ids - The IDs of contact branches (null because it's specified as null in the original type)
 * @property {number | null} user_id - The user ID
 * @property {number | null} owner_id - The owner ID
 * @property {Date | string} updated_at - The date when the contact was last updated
 */
export type DTOBexioGETContact = Omit<DTOBexioPOSTContact, "titel_id"> & {
    title_id: number | null;
};

export type BexioInvoicePositionCustomExtended = {
    amount: string;
    unit_id: number;
    account_id: number;
    tax_id: number;
    text: string;
    unit_price: string;
    discount_in_percent: string;
    type: "KbPositionCustom";
    parent_id: number | null;
};
export type BexioInvoicePositionArticleExtended = BexioInvoicePositionCustomExtended & {
    article_id: number;
    type: "KbPositionArticle";
};
export type BexioInvoicePositionTextExtended = {
    text: string;
    type: "KbPositionText";
    show_pos_nr: boolean;
    parent_id: number | null;
};
export type BexioInvoicePositionSubtotalExtended = {
    text: string;
    type: "KbPositionSubtotal";
};
export type BexioInvoicePositionPagebreakExtended = {
    pagebreak: boolean;
    type: "KbPositionPagebreak";
};
export type BexioInvoicePositionDiscountExtended = {
    text: string;
    is_percentual: boolean;
    value: string;
    type: "KbPositionDiscount";
};

/**
 * @typedef {object} DTOBexioGetInvoice
 *
 * @property {number} id - The invoice ID
 * @property {string} document_nr - The document number
 * @property {string | null} title - The title
 * @property {number | null} contact_id - The contact ID
 * @property {number | null} contact_sub_id - The sub-contact ID
 * @property {number} user_id - The user ID
 * @property {number | null} project_id - The project ID
 * @property {number} logopaper_id - The logopaper ID (deprecated)
 * @property {number} language_id - The language ID
 * @property {number} bank_account_id - The bank account ID
 * @property {number} currency_id - The currency ID
 * @property {number} payment_type_id - The payment type
 * @property {string} header - The header
 * @property {string} footer - The footer
 * @property {string} total_gross - The total gross
 * @property {string} total_net - The total net
 * @property {string} total_taxes - The total taxes
 * @property {string} total_received_payments - The total received payments
 * @property {string} total_credit_vouchers - The total credit vouchers
 * @property {string} total_remaining_payments - The total remaining payments
 * @property {string} total - The total
 * @property {number} total_rounding_difference - The total rounding difference
 * @property {number} mwst_type - The MWST type
 * @property {boolean} mwst_is_net - Indicates if MWST is net
 * @property {boolean} show_position_taxes - Indicates if position taxes are shown
 * @property {string | Date} is_valid_from - The valid from date
 * @property {string | Date} is_valid_to - The valid to date
 * @property {string} contact_address - The contact address
 * @property {number} kb_item_status_id - The KB item status ID
 * @property {string | null} reference - The reference
 * @property {string | null} api_reference - The API reference
 * @property {string | null} viewed_by_client_at - The viewed by client at date
 * @property {string} updated_at - The updated at date-time
 * @property {number} esr_id - The ESR ID
 * @property {number} qr_invoice_id - The QR invoice ID
 * @property {string | null} template_slug - The template slug
 * @property {Array<object>} taxs - The taxes array
 * @property {string | null} network_link - The network link
 * @property {Array<object} positions - The positions array
 */
export type DTOBexioGetInvoice = {
    id: number;
    document_nr: string;
    title: string | null;
    contact_id: number | null;
    contact_sub_id: number | null;
    user_id: number;
    project_id: number | null;
    logopaper_id: number | null; // deprecated
    language_id: BexioLanguageId;
    bank_account_id: number;
    currency_id: number;
    payment_type_id: BexioPaymentType;
    header: string;
    footer: string;
    total_gross: string;
    total_net: string;
    total_taxes: string;
    total_received_payments: string;
    total_credit_vouchers: string;
    total_remaining_payments: string;
    total: string;
    total_rounding_difference: number;
    mwst_type: BexioMWSTType;
    mwst_is_net: boolean;
    show_position_taxes: boolean;
    is_valid_from: string | Date;
    is_valid_to: string | Date;
    contact_address: string;
    kb_item_status_id: BexioItemStatusId;
    reference: string | null;
    api_reference: string | null;
    viewed_by_client_at: string | null;
    updated_at: string; // date-time
    esr_id: number;
    qr_invoice_id: number;
    template_slug: string | null;
    taxs: Array<object>;
    network_link: string | null;
    positions: Array<
        | BexioInvoicePositionArticleExtended
        | BexioInvoicePositionCustomExtended
        | BexioInvoicePositionTextExtended
        | BexioInvoicePositionSubtotalExtended
        | BexioInvoicePositionPagebreakExtended
        | BexioInvoicePositionDiscountExtended
    >;
};

export type TCalculationMethodRender = {
    id: "crealo" | "file";
    name: string;
};

/**
 * Allows us to extract specific options from a type
 * Like: SubsetOf<TPortalType, "book" | "press">
 */
export type SubsetOf<T, U extends T> = U;

export type TDatesRangeType = "startDate" | "endDate";
export interface IDatesRange {
    startDate: Date;
    endDate: Date;
}

export enum EStatementType {
    GROSS = "gross",
    NET = "net",
    PRECOUNT = "precount",
    CONSOLIDATED = "consolidated",
    MERGED = "merged",
    COLLECTIVE_MANAGEMENT = "collectiveManagement",
}

export enum EPaymentStatus {
    PAID = 2,
    TO_PAY = 1,
    NOT_PAID = 0,
}

export type TSortOrder = "asc" | "desc";
export type Binary = 0 | 1;

export type AnalyticsFilters = {
    sortColumn?: string;
    sortDirection?: "asc" | "desc";
    limit?: number;
    page?: number;
    titleIds?: (string | number)[];
    channelIds?: (string | number)[];
    periodIds?: string[] | number[];
    countryMarkets?: string[] | number[];
    dates?: IDatesRange;
};

export type TAccountStatus = "pending" | "noaccount" | "confirmed";
export type LogMethod = "GET" | "PUT" | "POST" | "all";

export interface ILog {
    id: number;
    email: string;
    companyId: number;
    endpoint: string;
    method: LogMethod;
    userAgent: string;
    ipAddress: string;
    payload: { query: JSON; params: JSON; body: JSON };
    origin: string;
    created: string;
    userId: number;
    userType: ROLES;
}

export type TImportTargets =
    | "title"
    | "article"
    | "beneficiar"
    | "sales"
    | "terms"
    | "advancePayments"
    | "stocks"
    | "provision"
    | "compute-analytics"
    | "handle-interforum-files"
    | "process-interforum-edi"
    | "subscription-channel-recalculate"
    | "compute-period-royalties"
    | "works";

export enum ImportTargetsEnum {
    title = "title",
    article = "article",
    beneficiar = "beneficiar",
    sales = "sales",
    terms = "terms",
    advancePayments = "advancePayments",
    stocks = "stocks",
    provision = "provision",
    analytics = "compute-analytics",
    handleInterforumFiles = "handle-interforum-files",
    processInterforumEdi = "process-interforum-edi",
    subscriptionChannelRecalculate = "subscription-channel-recalculate",
    computePeriodRoyalties = "compute-period-royalties",
    works = "works",
}

export const BookImportTargetsArray = [
    ImportTargetsEnum.title,
    ImportTargetsEnum.beneficiar,
    ImportTargetsEnum.sales,
    ImportTargetsEnum.terms,
    ImportTargetsEnum.advancePayments,
    ImportTargetsEnum.stocks,
    ImportTargetsEnum.provision,
    ImportTargetsEnum.works,
];

export const PressImportTargetsArray = [ImportTargetsEnum.article, ImportTargetsEnum.beneficiar];

export enum ExportTargetsEnum {
    article = "article",
    salesByChannels = "salesByChannels",
    management = "management",
    beneficiaries = "beneficiaries",
    titles = "titles",
    terms = "terms",
    advancePayments = "advancePayments",
    paymentTemplate = "paymentTemplate",
    stocks = "stocks",
    bankPayment = "bankPayment",
    das2 = "DAS2",
    contributions = "URSSAF",
    accounting = "accounting",
    sales = "sales",
    transferees = "transferees",
    transferTermSales = "transferTermSales",
    transferTerms = "transferTerms",
    titlesEan13MatchingChannel = "titlesEan13MatchingChannel",
    taxDeclaration = "taxDeclaration",
}

export const BookExportTargetsArray = [
    ExportTargetsEnum.salesByChannels,
    ExportTargetsEnum.management,
    ExportTargetsEnum.beneficiaries,
    ExportTargetsEnum.titles,
    ExportTargetsEnum.terms,
    ExportTargetsEnum.advancePayments,
    ExportTargetsEnum.stocks,
    ExportTargetsEnum.bankPayment,
    ExportTargetsEnum.das2,
    ExportTargetsEnum.contributions,
    ExportTargetsEnum.accounting,
    ExportTargetsEnum.sales,
    ExportTargetsEnum.titlesEan13MatchingChannel,
    ExportTargetsEnum.taxDeclaration,
];

export const PressExportTargetsArray = [
    ExportTargetsEnum.article,
    ExportTargetsEnum.management,
    ExportTargetsEnum.beneficiaries,
    ExportTargetsEnum.das2,
    ExportTargetsEnum.accounting,
    ExportTargetsEnum.contributions,
];

export const TransfereeExportTargetsArray = [ExportTargetsEnum.transferees, ExportTargetsEnum.transferTerms];

export type TExportTargets =
    | "article"
    | "salesByChannels"
    | "management"
    | "beneficiaries"
    | "titles"
    | "terms"
    | "advancePayments"
    | "paymentTemplate"
    | "stocks"
    | "bankPayment"
    | "DAS2"
    | "URSSAF"
    | "accounting"
    | "sales"
    | "transferees"
    | "transferTermSales"
    | "transferTerms";

export type AccountType = "publisher" | "beneficiary";

export type TrackerEvent = "Page visit" | "Sign up" | "Button clicked";
export type TrackerEventData<T extends TrackerEvent> = T extends "Page visit"
    ? { name: string }
    : T extends "Button clicked"
      ? { button: string }
      : T extends "Sign up"
        ? Record<string, never>
        : never;

export type TrackedUser = {
    username: string;
    name: string;
    email: string;
    companyId: string;
    companyName: string;
    accountType: AccountType | "admin";
};
export interface Tracker {
    trackEvent: <T extends TrackerEvent>(event: T, data: TrackerEventData<T>) => Promise<void>;
    registerSession: (user: TrackedUser) => Promise<void>;
    unregisterSession: () => Promise<void>;
}

export type ImportIndex = {
    bucket: string;
    fileKey: string;
    mapping: Record<string, string>;
    channelId?: number | string;
    resourceType: string;
    uploadedBy: number;
    delimiter?: string;
    /** Passed when job was created in GET multipart (avoids duplicate creation in PUT index-file) */
    processingJobId?: number;
};
